import React, { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import Field from './Field';
import Constants from '../../utils/Constants';
import {
    getCanvasContext,
    getInputMaxWidth,
    getInputMaxWidthWithoutLineBreak,
    getTextFont,
    getTextWidth
} from '../../utils/DOM';
import ConfirmButton from '../buttons/ConfirmButton';
import './_styles_/TextField.css';
import { extendedStateType } from '../../types';
import { resolveValue } from '../../utils/fields';
import { NO_STATE } from '../../hooks/useExtendedState';
import { onActionKey } from '../../utils';
import useEffectOnLoad from "../../hooks/useEffectOnLoad";

TextField.propTypes = {
    disabled: PropTypes.bool,
    label: PropTypes.node,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    inputId: PropTypes.string,
    onChange: PropTypes.func,
    validator: PropTypes.func,
    onValidated: PropTypes.func,
    autoComplete: PropTypes.string,
    className: PropTypes.string,
    wrapperClassName: PropTypes.string,
    type: PropTypes.string,
    postfix: PropTypes.node,
    placeholder: PropTypes.string,
    maxLength: PropTypes.number,
    onReset: PropTypes.func,
    isChanged: PropTypes.bool,
    isRequired: PropTypes.bool,
    inputMode: PropTypes.string,
    maximizeInputWidth: PropTypes.bool,
    useDynamicWidth: PropTypes.bool,
    showCleanup: PropTypes.bool,
    state: extendedStateType,
    valueTransformer: PropTypes.func,
    invokeValidatorOnRender: PropTypes.bool
};

function TextField(props) {
    const {
        disabled = false,
        label = null,
        inputId = null,
        value = undefined,
        onChange = undefined,
        autoComplete = null,
        className = '',
        wrapperClassName = '',
        validator = null,
        onValidated = undefined,
        type = 'text',
        postfix = null,
        placeholder = null,
        maxLength = null,
        onReset = undefined,
        isChanged = undefined,
        isRequired = false,
        inputMode = null,
        maximizeInputWidth = false,
        useDynamicWidth = true,
        state = NO_STATE,
        showCleanup = false,
        valueTransformer = undefined,
        invokeValidatorOnRender = false
    } = props;
    const { t } = useTranslation();
    const [canvasContext] = useState(getCanvasContext());
    const [inputFont, setInputFont] = useState('normal normal normal normal sans-serif');
    const [maxWidth, setMaxWidth] = useState(0);
    const resolvedValue = resolveValue(value, state);
    const [displayedValue, setDisplayedValue] = useState(resolvedValue);
    const [error, setError] = useState(null);
    const stateValueSetter = state && state !== NO_STATE ? state.setValueIfNew : () => {};
    const debouncedValidation = useDebouncedCallback((newValue, sanitizedValue) => {
        validator && validator(newValue)
            .then(() => onValidationComplete(null, sanitizedValue))
            .catch((e) => onValidationComplete(e, sanitizedValue));
    }, Constants.DISPLAY_FIELD_ERROR_DELAY_IN_MS);
    const [inputElement, setInputElement] = useState(null);
    const [inputScrollLeft, setInputScrollLeft] = useState(0);
    const resetInputScrollLeft = () => setInputScrollLeft(0);
    const updateInputScrollLeft = () => setInputScrollLeft(inputElement.scrollLeft);
    const onValidationComplete = (newError, sanitizedValue) => {
        onValidated && onValidated(sanitizedValue, validator === null ? null : !newError);
        setError(newError);
    };
    const setNewValue = (newValue) => {
        setDisplayedValue(newValue);

        const currentValue = resolveValue(value, state);
        const sanitizedNewValue = sanitize(newValue);
        const resolvedNewValue = valueTransformer ? valueTransformer(sanitizedNewValue) : sanitizedNewValue;

        if (currentValue !== resolvedNewValue) {
            stateValueSetter(resolvedNewValue);
            onChange && onChange(resolvedNewValue);
        }

        if (validator) {
            debouncedValidation(newValue, resolvedNewValue);
        } else {
            onValidationComplete(null, resolvedNewValue);
        }
    };
    useEffectOnUpdate(() => {
        updateInputScrollLeft();
        setNewValue(resolveValue(value, state));
    }, [value, state.value]);
    useEffectOnLoad(() => {
        if (invokeValidatorOnRender && validator) {
            const currentValue = resolveValue(value, state);
            debouncedValidation(currentValue, sanitize(currentValue));
        }
    });
    const measuredInputRef = useCallback((node) => {
        if (node !== null) {
            setInputFont(getTextFont(node));
            const isInExpandedRow = node.closest('.rich-list-row-expanded');
            if (isInExpandedRow) {
                setMaxWidth(getInputMaxWidth(node));
            }
            setInputElement(node);
        }
    }, []);
    const onChangeHandler = (event) => {
        setNewValue(event.target.value);
    };
    const [dynamicInputWidth, setDynamicInputWidth] = useState(null);
    const [overlayLeft, setOverlayLeft] = useState(null);
    const [overflowPartVisible, setOverflowPartVisible] = useState(false);
    const [overlayWidth, setOverlayWidth] = useState(null);
    const computeDynamicInputWidthInPx = useCallback((textWidth) => (maxWidth ? Math.min(textWidth, maxWidth) : textWidth) + Constants.DYNAMIC_WIDTH_GAP_IN_PX, [maxWidth]);
    useEffect(() => {
        if (maximizeInputWidth) {
            if (inputElement) {
                setDynamicInputWidth(`${getInputMaxWidthWithoutLineBreak(inputElement)}px`);
                return;
            }
        }

        if (!useDynamicWidth || !displayedValue) {
            setDynamicInputWidth(null);
            return;
        }

        const textWidth = getTextWidth(canvasContext, inputFont, displayedValue);
        if (!textWidth) {
            return;
        }

        const dynamicInputWidthInPx = computeDynamicInputWidthInPx(textWidth);
        setDynamicInputWidth(`${dynamicInputWidthInPx}px`);

        if (maxLength && displayedValue.length > maxLength) {

            let overlayLeft = null;
            let overlayWidth = null;

            const maxLengthTextWidth = getTextWidth(canvasContext, inputFont, displayedValue.substring(0, maxLength));
            const overflowPartIsVisible = maxLengthTextWidth < (inputScrollLeft + maxWidth);
            setOverflowPartVisible(overflowPartIsVisible);

            if (overflowPartIsVisible) {
                let left = 0;
                let overlayWidthBase = maxWidth;

                if (textWidth <= maxWidth) {
                    left = maxLengthTextWidth;
                    overlayWidthBase = textWidth;
                } else if (inputScrollLeft < maxLengthTextWidth) {
                    left = maxLengthTextWidth - inputScrollLeft;
                    overlayWidthBase = maxWidth;
                }

                overlayLeft = `${left}px`;
                overlayWidth = `${overlayWidthBase - left}px`;
            }

            setOverlayLeft(overlayLeft);
            setOverlayWidth(overlayWidth);
        }
    }, [inputScrollLeft, displayedValue, canvasContext, inputFont, maxLength, maxWidth, useDynamicWidth, error, computeDynamicInputWidthInPx, inputElement, maximizeInputWidth]);
    const onRemoveOverflowingText = () => setNewValue(displayedValue.substring(0, maxLength));
    const doCleanup = () => setNewValue('');

    let cleanupTrigger = null;
    if (displayedValue) {
        cleanupTrigger = (
            <div
                className="text-field-cleanup-button"
                title={t('misc.text-field-cleanup')}
                aria-label={t('misc.text-field-cleanup')}
                onClick={doCleanup}
                onKeyUp={onActionKey(doCleanup)}
                role="button"
                tabIndex={-1}
            >
                <FontAwesomeIcon icon="times" />
            </div>
        );
    }

    return (
        <Field
            label={label}
            className={wrapperClassName}
            postfix={postfix}
            inputId={inputId}
            error={error}
            onReset={onReset}
            isChanged={isChanged}
            state={state}
            isRequired={isRequired}
            isRequiredEmpty={isRequired && !displayedValue}
        >
            <input
                ref={measuredInputRef}
                type={type}
                id={inputId}
                className={className}
                value={displayedValue || ''}
                onChange={onChangeHandler}
                onKeyUp={updateInputScrollLeft}
                onBlur={resetInputScrollLeft}
                autoComplete={autoComplete}
                placeholder={placeholder}
                inputMode={inputMode}
                aria-required={isRequired}
                style={{ width: dynamicInputWidth }}
                disabled={disabled}
            />
            {showCleanup && cleanupTrigger}
            {maxLength && displayedValue && displayedValue.length > maxLength && (
                <div
                    className={`max-length-overlay${!overflowPartVisible ? ' overflow-part-not-visible' : ''}`}
                    style={{
                        left: overlayLeft,
                        width: overlayWidth,
                    }}
                >
                    <ConfirmButton
                        onConfirm={onRemoveOverflowingText}
                        message={t('misc.remove-overflowing-text')}
                        className="remove-text-button"
                        title={t('button.remove-overflowing-text')}
                        tabIndex={-1}
                    >
                        <FontAwesomeIcon icon="times" />
                        {!overflowPartVisible && (
                            <div className="arrows">
                                <FontAwesomeIcon icon="caret-right" />
                                <FontAwesomeIcon icon="caret-right" />
                                <FontAwesomeIcon icon="caret-right" />
                            </div>
                        )}
                    </ConfirmButton>
                </div>
            )}
        </Field>
    );
}

function sanitize(value) {
    return value ? value.trim() : value;
}

export default memo(TextField);
